import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Domain } from '../types';
import { showToast } from '../utils/toast';

type DbDomain = Database['public']['Tables']['domains']['Row'];

let fetchPromise: Promise<Domain[]> | null = null;

export const domainService = {
  getDomains(): Promise<Domain[]> {
    if (fetchPromise) return fetchPromise;

    fetchPromise = (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('Vous devez être connecté pour charger les domaines.');

        const { data, error } = await supabase
          .from('domains')
          .select('*')
          .order('created_at', { ascending: true });

        if (error) {
          console.error('[domain.service] getDomains (fetch):', error.message);
          showToast('Erreur lors du chargement des domaines', 'error');
          throw error;
        }

        let finalData = data || [];

        // 1. Auto-seed if empty
        if (finalData.length === 0) {
          const defaultDomains = [
            { name: 'Travail', color: 'bg-blue-500/20 text-blue-400', user_id: user.id },
            { name: 'Personnel', color: 'bg-emerald-500/20 text-emerald-400', user_id: user.id },
            { name: 'Santé & Sport', color: 'bg-orange-500/20 text-orange-400', user_id: user.id },
            { name: 'Finances', color: 'bg-purple-500/20 text-purple-400', user_id: user.id },
          ];

          const { data: inserted, error: insertError } = await supabase
            .from('domains')
            .insert(defaultDomains)
            .select('*');

          if (insertError) {
            // Race condition probable : session JWT pas encore propagée
            console.warn(
              '[domain.service] Auto-seed failed, retrying in 1.5s...',
              insertError.message
            );
            await new Promise((r) => setTimeout(r, 1500));

            // Re-check : peut-être que les domaines existent maintenant
            const { data: recheckData } = await supabase
              .from('domains')
              .select('*')
              .order('created_at', { ascending: true });

            if (recheckData && recheckData.length > 0) {
              console.log('[domain.service] Domains found on recheck, skipping seed');
              finalData = recheckData;
            } else {
              // Dernier essai
              const { data: retryInserted, error: retryError } = await supabase
                .from('domains')
                .insert(defaultDomains)
                .select('*');

              if (retryError) {
                console.error('[domain.service] getDomains (auto-seed retry):', retryError.message);
                showToast('Impossible de créer les domaines par défaut', 'error');
                throw retryError;
              }
              console.log('[domain.service] Auto-seed retry successful');
              finalData = retryInserted || [];
            }
          } else {
            finalData = inserted || [];
          }
        } else {
          // 2. Auto-heal: Detect and remove duplicates created by previous strict-mode races
          const seenNames = new Set<string>();
          const idsToDelete: string[] = [];
          const uniqueDomains: typeof data = [];

          for (const d of finalData) {
            if (seenNames.has(d.name)) {
              idsToDelete.push(d.id);
            } else {
              seenNames.add(d.name);
              uniqueDomains.push(d);
            }
          }

          if (idsToDelete.length > 0) {
            supabase
              .from('domains')
              .delete()
              .in('id', idsToDelete)
              .then(({ error }) => {
                if (error) console.error('Échec du nettoyage des domaines dupliqués:', error);
              });
          }
          finalData = uniqueDomains;
        }

        return finalData.map((d) => ({
          id: d.id,
          name: d.name,
          color: d.color || 'bg-zinc-500/20 text-zinc-400',
        }));
      } finally {
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  },
};
